import Lenis from 'lenis';
import { gsap } from 'gsap';

import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SmoothScroll from '../singleton/SmoothScroll';
import WindowSize from '../singleton/WindowSize';

gsap.registerPlugin(ScrollTrigger);

class Header {
  private windowSize: WindowSize;
  $header: JQuery<HTMLElement>;
  $body: JQuery<HTMLElement>;
  $hamburger: JQuery<HTMLElement>;
  $nav: JQuery<HTMLElement>;
  $parentMenuItems: JQuery<HTMLElement>;
  // $dropdownButtons: JQuery<HTMLElement>;
  $subMenus: JQuery<HTMLElement>;
  $menuLinks: JQuery<HTMLElement>;

  headerIsOpened: boolean;
  isScrollingDisabled: boolean;
  isDark: boolean;
  isHome: boolean;
  threshold: number;
  smoothScroll: typeof SmoothScroll;

  constructor() {
    this.$header = $('.header');
    this.$body = $('body');
    this.$hamburger = this.$header.find('.header__hamburger');
    this.$nav = this.$header.find('.header__nav');
    this.$parentMenuItems = this.$nav.find('.menu-item-has-children > a');
    // this.$dropdownButtons = this.$nav.find('.header__dropdown-button');
    this.$subMenus = this.$nav.find('.sub-menu');
    this.$subMenus.attr('aria-expanded', 'false');
    this.$menuLinks = this.$nav.find('a');

    this.headerIsOpened = false;
    this.isScrollingDisabled = false;
    this.isDark = false;
    this.isHome = this.$body.hasClass('home');
    this.threshold = 50;

    this.windowSize = WindowSize.getInstance();
    this.smoothScroll = SmoothScroll;

    this.init();
  }

  init() {
    this.$hamburger.on('click', this.onHamburgerClick.bind(this));
    this.smoothScroll.scroll.on('scroll', this.onScroll.bind(this));
    this.windowSize.addListener(this.onResize.bind(this));
    this.$parentMenuItems.on('click', this.dropdownButtonClick.bind(this));
    this.$nav.find('a').on('keydown', this.keyboardNavigation.bind(this));
  }

  onHamburgerClick() {
    if (this.windowSize.getSize().width >= 768) {
      return;
    }

    if (!this.headerIsOpened) {
      this.openNav();
    } else {
      this.closeNav();
    }
  }

  keyboardNavigation(e: JQuery.KeyDownEvent) {
    const { key } = e;

    // Key handling for improved keyboard navigation
    if (
      ![
        'Enter',
        'ArrowUp',
        'ArrowRight',
        'ArrowLeft',
        'ArrowDown',
        'Tab',
        'Escape',
        ' ',
      ].includes(key)
    ) {
      return;
    }

    const $currentTarget = $(e.currentTarget); // link
    const $parentLi = $currentTarget.parent();
    const $parentList = $parentLi.parent('ul'); // li
    const isSubMenu = $parentList.hasClass('sub-menu'); // check if it's a submenu
    const hasSubMenu = $parentLi.find('.sub-menu').length > 0; // check if it has a submenu

    switch (key) {
      // Closes any open submenu and returns focus to its parent.
      // If at top level: may close the entire menu (optional depending on design).
      case 'Escape': {
        e.preventDefault();
        e.stopPropagation();

        if (isSubMenu) {
          const $parentItem = $parentList.parent();
          this.closeSubmenu($parentList);
          $parentItem.children('a').trigger('focus');
        }
        break;
      }

      // On menubar item: moves focus to the previous top-level item.
      // In a submenu, closes the submenu and returns focus to its parent item.
      case 'ArrowLeft': {
        e.preventDefault();

        if (isSubMenu) {
          const $parentItem = $parentList.parent();
          this.closeSubmenu($parentList);
          $parentItem.children('a').trigger('focus');
        } else {
          const $prevItem = $parentLi.prev('li').children('a');
          if ($prevItem.length) {
            $prevItem.trigger('focus');
          } else {
            // If no previous item, focus the last item in the menu
            const $lastItem = $parentList.children('li').last().children('a');
            $lastItem.trigger('focus');
          }
        }

        break;
      }

      // On menubar item: moves focus to the next top-level item.
      // If the item has a submenu, optionally open it.
      case 'ArrowRight': {
        e.preventDefault();

        if (isSubMenu) {
          const $parentItem = $parentList.parent();
          this.closeSubmenu($parentList);
          $parentItem.children('a').trigger('focus');
        } else {
          const $nextItem = $parentLi.next('li').children('a');
          if ($nextItem.length) {
            $nextItem.trigger('focus');
          } else {
            // If no next item, focus the first item in the menu
            const $firstItem = $parentList.children('li').first().children('a');
            $firstItem.trigger('focus');
          }
        }
        break;
      }

      // On menubar item: opens submenu (if exists) and focuses first item.
      // On submenu item: moves focus to the next item in the submenu.
      case 'ArrowDown': {
        e.preventDefault();

        if (isSubMenu) {
          const $nextItem = $parentLi.next('li').children('a');
          if ($nextItem.length) {
            $nextItem.trigger('focus');
          } else {
            // If no next item, focus the first item in the menu
            const $firstItem = $parentList.children('li').first().children('a');
            $firstItem.trigger('focus');
          }
        } else if (hasSubMenu) {
          const $submenu = $parentLi.children('.sub-menu');
          if ($submenu.length) {
            this.openSubmenu($submenu);
            $submenu.find('a').first().trigger('focus');
          } else {
            const $nextItem = $parentLi.next('li').children('a');
            if ($nextItem.length) {
              $nextItem.trigger('focus');
            } else {
              // If no next item, focus the first item in the menu
              const $firstItem = $parentList
                .children('li')
                .first()
                .children('a');
              $firstItem.trigger('focus');
            }
          }
        }
        break;
      }

      // On submenu item: moves focus to the previous item.
      // If on the first submenu item, wraps to the last one.
      case 'ArrowUp': {
        e.preventDefault();

        if (isSubMenu) {
          const $prevItem = $parentLi.prev('li').children('a');
          if ($prevItem.length) {
            $prevItem.trigger('focus');
          } else {
            // If no previous item, focus the last item in the menu
            const $lastItem = $parentList.children('li').last().children('a');
            $lastItem.trigger('focus');
          }
        } else if (hasSubMenu) {
          const $submenu = $parentLi.children('.sub-menu');
          if ($submenu.length) {
            this.openSubmenu($submenu);
            $submenu.find('a').last().trigger('focus');
          }
        } else {
          const $prevItem = $parentLi.prev('li').children('a');
          if ($prevItem.length) {
            $prevItem.trigger('focus');
          } else {
            // If no previous item, focus the last item in the menu
            const $lastItem = $parentList.children('li').last().children('a');
            $lastItem.trigger('focus');
          }
        }
        break;
      }

      case 'Enter':
      case ' ': {
        e.preventDefault();

        if (hasSubMenu) {
          if ($parentLi.hasClass('is-opened')) {
            // if submenu is opened, navigate to the link
            const $link = $parentLi.children('a');
            if ($link.length) {
              $link.get(0)?.click();
            }
          } else {
            const $submenu = $parentLi.children('.sub-menu');
            if ($submenu.length) {
              this.openSubmenu($submenu);
              $submenu.find('a').first().trigger('focus');
            }
          }
        } else {
          // If it's a link, allow default behavior
          const $link = $parentLi.children('a');

          if ($link.length) {
            $link.get(0)?.click();
          }
        }
        break;
      }

      case 'Tab': {
        // if tabbing out of submenu, close it
        if (isSubMenu) {
          const $submenu = $parentLi.closest('.sub-menu');
          const isLastItem = $parentLi.is(':last-child');

          if (isLastItem) {
            this.closeSubmenu($submenu);
            return;
          }

          // check if there is other opened submenu
          const $otherSubmenus = this.$subMenus.not($submenu);
          if ($otherSubmenus.length) {
            $otherSubmenus.each((index, submenu) => {
              const $submenu = $(submenu);
              this.closeSubmenu($submenu);
            });
          }
        }
      }

      default:
        // Ignore any other keys
        break;
    }
  }

  dropdownButtonClick(e: JQuery.ClickEvent) {
    e.preventDefault();

    const $target = $(e.currentTarget);
    const $parent = $target.closest('li');
    const $submenu = $parent.find('.sub-menu');

    if ($parent.hasClass('is-opened')) {
      this.closeSubmenu($submenu);
    } else {
      this.openSubmenu($submenu);
    }
  }

  openSubmenu($submenu: JQuery<HTMLElement>) {
    const $parent = $submenu.parent();

    if (this.windowSize.getSize().width < 1024) {
      this.$subMenus.slideUp();
      $submenu.slideDown();
    }

    this.$subMenus.attr('aria-expanded', 'false');
    this.$parentMenuItems.parent().removeClass('is-opened');
    $parent.addClass('is-opened');
    $submenu.attr('aria-expanded', 'true');
  }

  closeSubmenu($submenu: JQuery<HTMLElement>) {
    const $parent = $submenu.parent();
    $parent.removeClass('is-opened');

    if (this.windowSize.getSize().width < 1024) {
      $submenu.slideUp();
    }

    $submenu.attr('aria-expanded', 'false');
  }

  openNav() {
    this.smoothScroll.disableScrolling();
    this.$nav.attr('aria-expanded', 'true');
    this.$hamburger.attr('aria-expanded', 'true');
    this.$header.addClass('is-opened');

    this.headerIsOpened = true;
    this.isScrollingDisabled = true;
  }

  closeNav() {
    this.$nav.attr('aria-expanded', 'false');
    this.$hamburger.attr('aria-expanded', 'false');
    this.$header.removeClass('is-opened');

    if (this.isScrollingDisabled) {
      this.smoothScroll.enableScrolling();
      this.isScrollingDisabled = false;
    }

    this.headerIsOpened = false;
  }

  onResize() {
    if (this.headerIsOpened) {
      this.closeNav();
    }

    const { width } = this.windowSize.getSize();
    if (width < 1024) {
      this.$subMenus.slideUp();
    } else {
      this.$subMenus.show();
    }
  }

  onScroll(e: Lenis) {
    const st = e.scroll;

    if (e.isStopped) return;

    if (e.direction === -1) {
      this.$header.addClass('is-scrolling-up');
    } else {
      this.$header.removeClass('is-scrolling-up');
    }

    if (st > this.threshold) {
      this.$header.addClass('is-scrolling');
    } else {
      this.$header.removeClass('is-scrolling');
    }
  }
}

export default Header;
